import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, ArrowLeft, RefreshCw, TrendingUp, TrendingDown,
  Signal, Zap, Target, Award
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quality grade colors
const getGradeColor = (grade) => {
  switch(grade) {
    case 'A+': return 'bg-emerald-500 text-white';
    case 'A': return 'bg-green-500 text-white';
    case 'B': return 'bg-blue-500 text-white';
    case 'C': return 'bg-yellow-500 text-white';
    case 'D': return 'bg-orange-500 text-white';
    case 'E': return 'bg-red-500 text-white';
    case 'F': return 'bg-red-700 text-white';
    default: return 'bg-gray-500 text-white';
  }
};

const NetworkQualityMetrics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [customerMetrics, setCustomerMetrics] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [timeRange, setTimeRange] = useState(24); // hours

  useEffect(() => {
    fetchQualityData();
    // Auto-refresh every 2 minutes
    const interval = setInterval(fetchQualityData, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedCustomer) {
      fetchCustomerHistory(selectedCustomer.customer_id);
    }
  }, [selectedCustomer, timeRange]);

  const fetchQualityData = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('monitoringAdminToken');

      // Fetch statistics
      const statsResponse = await fetch(`${API_URL}/api/monitoring/quality/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStatistics(statsData.statistics);
      }

      // Fetch latest metrics for all customers
      const metricsResponse = await fetch(`${API_URL}/api/monitoring/quality/latest`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (metricsResponse.ok) {
        const metricsData = await metricsResponse.json();
        setCustomerMetrics(metricsData.metrics || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching quality data:', error);
      toast.error('Failed to fetch quality metrics');
    } finally {
      setRefreshing(false);
    }
  };

  const fetchCustomerHistory = async (customerId) => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(
        `${API_URL}/api/monitoring/quality/customer/${customerId}?hours=${timeRange}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Format data for charts
        const formattedData = data.metrics.map(m => ({
          time: new Date(m.timestamp).toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false
          }),
          latency: m.latency,
          packet_loss: m.packet_loss,
          jitter: m.jitter,
          quality_score: m.quality_score,
          download_speed: m.download_speed,
          upload_speed: m.upload_speed
        })).reverse(); // Oldest to newest for chart
        
        setCustomerHistory(formattedData);
      }
    } catch (error) {
      console.error('Error fetching customer history:', error);
    }
  };

  const runQualityCheckNow = async () => {
    try {
      setRefreshing(true);
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring/quality/run-check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Quality check completed for ${data.checked} customers`);
        await fetchQualityData();
      }
    } catch (error) {
      toast.error('Failed to run quality check');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading quality metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-6">
      {/* Header */}
      <div className="mb-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/monitoring-dashboard')}
              className="text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            {/* Netix Logo */}
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/netix-logo-horizontal.png" 
                alt="Netix Fibernet" 
                className="h-8 w-auto"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Signal className="h-8 w-8" />
                Network Quality Metrics
              </h1>
              <p className="text-blue-100 mt-1">Real-time network performance monitoring</p>
            </div>
          </div>
          <Button
            onClick={runQualityCheckNow}
            disabled={refreshing}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Checking...' : 'Run Check Now'}
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-emerald-500 to-green-500 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Award className="h-4 w-4" />
                Avg Quality Score
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{statistics.avg_quality_score}</div>
              <p className="text-xs text-white/80 mt-1">Out of 100</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Quality Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-emerald-600">● Excellent</span>
                  <span className="font-semibold">{statistics.excellent_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-blue-600">● Good</span>
                  <span className="font-semibold">{statistics.good_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-600">● Poor</span>
                  <span className="font-semibold">{statistics.poor_count}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Avg Latency
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{statistics.avg_latency} ms</div>
              <p className="text-xs text-gray-500 mt-1">Lower is better</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Target className="h-4 w-4" />
                Avg Packet Loss
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{statistics.avg_packet_loss}%</div>
              <p className="text-xs text-gray-500 mt-1">Lower is better</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Customer Quality List */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Quality Scores</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {customerMetrics.map((metric) => (
                <div
                  key={metric.customer_id}
                  onClick={() => setSelectedCustomer(metric)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    selectedCustomer?.customer_id === metric.customer_id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900 block">{metric.customer_name}</span>
                      <span className="text-xs text-gray-500">{metric.static_ip}</span>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getGradeColor(metric.quality_grade)}`}>
                      {metric.quality_grade}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Latency:</span>
                      <span className="ml-1 font-semibold">{metric.latency}ms</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Loss:</span>
                      <span className="ml-1 font-semibold">{metric.packet_loss}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Jitter:</span>
                      <span className="ml-1 font-semibold">{metric.jitter}ms</span>
                    </div>
                  </div>
                  {/* Quality Score Progress Bar */}
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          metric.quality_score >= 90 ? 'bg-emerald-500' :
                          metric.quality_score >= 80 ? 'bg-green-500' :
                          metric.quality_score >= 70 ? 'bg-blue-500' :
                          metric.quality_score >= 60 ? 'bg-yellow-500' :
                          'bg-red-500'
                        }`}
                        style={{ width: `${metric.quality_score}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-500 mt-1">Score: {metric.quality_score}/100</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Customer History Charts */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {selectedCustomer ? `${selectedCustomer.customer_name} - History` : 'Select a Customer'}
              </CardTitle>
              {selectedCustomer && (
                <select
                  value={timeRange}
                  onChange={(e) => setTimeRange(Number(e.target.value))}
                  className="border rounded-md px-3 py-1 text-sm"
                >
                  <option value={6}>Last 6 Hours</option>
                  <option value={12}>Last 12 Hours</option>
                  <option value={24}>Last 24 Hours</option>
                  <option value={72}>Last 3 Days</option>
                </select>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedCustomer ? (
              <div className="h-[550px] flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <Signal className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Select a customer to view detailed metrics</p>
                </div>
              </div>
            ) : customerHistory.length === 0 ? (
              <div className="h-[550px] flex items-center justify-center text-gray-400">
                <p>No history data available</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Quality Score Trend */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Quality Score Over Time</h4>
                  <ResponsiveContainer width="100%" height={120}>
                    <AreaChart data={customerHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{fontSize: 10}} />
                      <YAxis domain={[0, 100]} tick={{fontSize: 10}} />
                      <Tooltip />
                      <Area type="monotone" dataKey="quality_score" stroke="#3b82f6" fill="#93c5fd" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Latency & Jitter */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Latency & Jitter (ms)</h4>
                  <ResponsiveContainer width="100%" height={120}>
                    <LineChart data={customerHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{fontSize: 10}} />
                      <YAxis tick={{fontSize: 10}} />
                      <Tooltip />
                      <Legend wrapperStyle={{fontSize: '12px'}} />
                      <Line type="monotone" dataKey="latency" stroke="#3b82f6" strokeWidth={2} />
                      <Line type="monotone" dataKey="jitter" stroke="#f59e0b" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                {/* Packet Loss */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Packet Loss (%)</h4>
                  <ResponsiveContainer width="100%" height={100}>
                    <BarChart data={customerHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{fontSize: 10}} />
                      <YAxis tick={{fontSize: 10}} />
                      <Tooltip />
                      <Bar dataKey="packet_loss" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Speed */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Speed (Mbps)</h4>
                  <ResponsiveContainer width="100%" height={100}>
                    <LineChart data={customerHistory}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" tick={{fontSize: 10}} />
                      <YAxis tick={{fontSize: 10}} />
                      <Tooltip />
                      <Legend wrapperStyle={{fontSize: '12px'}} />
                      <Line type="monotone" dataKey="download_speed" stroke="#10b981" strokeWidth={2} name="Download" />
                      <Line type="monotone" dataKey="upload_speed" stroke="#8b5cf6" strokeWidth={2} name="Upload" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NetworkQualityMetrics;
